import { useEffect, useMemo, useRef, useState } from 'react'
import type { Action, ActionDraft } from '../domain/action'
import type { Area, AreaDraft } from '../domain/area'
import type { Specialization } from '../domain/specialization'
import { specializationNames } from '../domain/specialization'
import type { Objective, ObjectiveDraft } from '../domain/objective'
import { MissionLifecycleDialog, type MissionOperation } from '../presentation/components/MissionLifecycleDialog'
import { MissionJournalSheet } from '../presentation/components/MissionJournalSheet'
import { IndexedDbLeveleriaRepository } from '../infrastructure/db/indexedDbLeveleriaRepository'
import { ActionPreviewSheet } from '../presentation/components/ActionPreviewSheet'
import { ActionSheet } from '../presentation/components/ActionSheet'
import { ActiveActionCard } from '../presentation/components/ActiveActionCard'
import { AreaCard } from '../presentation/components/AreaCard'
import { AreaDetail } from '../presentation/components/AreaDetail'
import { AreaSheet } from '../presentation/components/AreaSheet'
import { LevelUpDialog } from '../presentation/components/LevelUpDialog'
import { SpecializationsSheet } from '../presentation/components/SpecializationsSheet'
import { ObjectiveEditorSheet } from '../presentation/components/ObjectiveEditorSheet'
import { ObjectivesSheet } from '../presentation/components/ObjectivesSheet'
import { ObjectiveActionsSheet } from '../presentation/components/ObjectiveActionsSheet'
import { ThemeSheet } from '../presentation/components/ThemeSheet'
import { HistorySheet } from '../presentation/components/HistorySheet'
import { RewardsSheet } from '../presentation/components/RewardsSheet'
import { RewardGoalCard } from '../presentation/components/RewardGoalCard'
import { OnboardingDialog } from '../presentation/components/OnboardingDialog'
import { AddIcon, BrandMark, CoinsIcon, CompassIcon, HistoryIcon, SettingsIcon } from '../presentation/components/Icons'
import { useAreaRoute } from '../presentation/hooks/useAreaRoute'
import { useLeveleria, type LevelUpEvent } from '../presentation/hooks/useLeveleria'
import { useTheme } from '../presentation/hooks/useTheme'
import { useI18n } from '../i18n'
import { trackEvent } from '../analytics'
import { missionCopy } from '../presentation/missionCopy'

export const App = () => {
  const { language, copy: { app: c } } = useI18n()
  const openedEventSent = useRef(false)
  const repository = useMemo(() => new IndexedDbLeveleriaRepository(), [])
  const leveleria = useLeveleria(repository)
  const { areas, actions, missionActions, addMissionNote, specializations, objectives, activityRecords, milestones, rewards, rewardRedemptions, targetRewardId, activeAction, coins, loading, saving, error, clearError, createArea, updateArea, deleteArea, createAction, updateAction, deleteAction, createSpecialization, deleteSpecialization, createObjective, updateObjective, activateObjective, completeObjective, closeObjective, repeatObjective, moveObjective, toggleActionObjective, activateAction, setActiveAmount, abandonActiveAction, createReward, deleteReward, setTargetReward, redeemReward, exportBackup, importBackup, completeActiveAction } = leveleria
  const [areaSheetOpen, setAreaSheetOpen] = useState(false)
  const [actionSheetOpen, setActionSheetOpen] = useState(false)
  const [themeSheetOpen, setThemeSheetOpen] = useState(false)
  const [historySheetOpen, setHistorySheetOpen] = useState(false)
  const [rewardsSheetOpen, setRewardsSheetOpen] = useState(false)
  const [specializationsSheetAreaId, setSpecializationsSheetAreaId] = useState<string | null>(null)
  const [objectivesSheetAreaId, setObjectivesSheetAreaId] = useState<string | null>(null)
  const [objectiveEditorAreaId, setObjectiveEditorAreaId] = useState<string | null>(null)
  const [editingObjective, setEditingObjective] = useState<Objective | null>(null)
  const [missionOperation, setMissionOperation] = useState<{ id: string; operation: MissionOperation } | null>(null)
  const operationObjective = objectives.find((item) => item.id === missionOperation?.id) ?? null
  const [journalObjectiveId, setJournalObjectiveId] = useState<string | null>(null)
  const journalObjective = objectives.find((item) => item.id === journalObjectiveId) ?? null
  const [linkObjectiveId, setLinkObjectiveId] = useState<string | null>(null)
  const [editingArea, setEditingArea] = useState<Area | null>(null)
  const [editingAction, setEditingAction] = useState<Action | null>(null)
  const [actionAreaId, setActionAreaId] = useState<string | null>(null)
  const [actionObjectiveId, setActionObjectiveId] = useState<string | null>(null)
  const [previewActionId, setPreviewActionId] = useState<string | null>(null)
  const [previewFromRandom, setPreviewFromRandom] = useState(false)
  const [levelUpEvent, setLevelUpEvent] = useState<LevelUpEvent | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [onboardingOpen, setOnboardingOpen] = useState(() => {
    try { return localStorage.getItem('leveleria.onboarding.completed') !== 'true' } catch { return true }
  })
  const { areaId, openArea, goHome } = useAreaRoute()
  const { mode: themeMode, selectMode: selectThemeMode } = useTheme()
  const selectedArea = areaId ? areas.find((area) => area.id === areaId) : null
  const selectedActions = selectedArea ? actions.filter((action) => action.areaId === selectedArea.id) : []
  const selectedSpecializations = selectedArea ? specializations.filter((item) => item.areaId === selectedArea.id) : []
  const selectedObjectives = selectedArea ? objectives.filter((item) => item.areaId === selectedArea.id) : []
  const previewAction = previewActionId ? actions.find((action) => action.id === previewActionId) ?? null : null
  const previewArea = previewAction ? areas.find((area) => area.id === previewAction.areaId) ?? null : null
  const activeActionData = activeAction ? actions.find((action) => action.id === activeAction.actionId) ?? null : null
  const activeAreaData = activeActionData ? areas.find((area) => area.id === activeActionData.areaId) ?? null : null
  const targetReward = targetRewardId ? rewards.find((reward) => reward.id === targetRewardId) ?? null : null
  const managedArea = specializationsSheetAreaId ? areas.find((area) => area.id === specializationsSheetAreaId) ?? null : null
  const managedSpecializations = managedArea ? specializations.filter((item) => item.areaId === managedArea.id) : []
  const managedActions = managedArea ? actions.filter((action) => action.areaId === managedArea.id) : []
  const objectivesArea = objectivesSheetAreaId ? areas.find((area) => area.id === objectivesSheetAreaId) ?? null : null
  const objectivesForArea = objectivesArea ? objectives.filter((item) => item.areaId === objectivesArea.id) : []
  const objectiveActions = objectivesArea ? actions.filter((action) => action.areaId === objectivesArea.id) : []
  const linkedObjective = linkObjectiveId ? objectives.find((item) => item.id === linkObjectiveId) ?? null : null
  const linkedArea = linkedObjective ? areas.find((area) => area.id === linkedObjective.areaId) ?? null : null
  const linkedActions = linkedArea ? actions.filter((action) => action.areaId === linkedArea.id) : []
  const linkedSpecializations = linkedArea ? specializations.filter((item) => item.areaId === linkedArea.id) : []
  const labelFor = (action: Action | null) => action ? specializationNames(action.categoryIds, specializations.filter((item) => item.areaId === action.areaId)).join(' + ') : ''

  useEffect(() => {
    if (openedEventSent.current) return
    openedEventSent.current = true
    trackEvent('app_opened', language)
  }, [language])

  const openCreate = () => {
    clearError()
    setEditingArea(null)
    setAreaSheetOpen(true)
  }

  const openEdit = (area: Area) => {
    clearError()
    setEditingArea(area)
    setAreaSheetOpen(true)
  }

  const closeAreaSheet = () => setAreaSheetOpen(false)

  const saveArea = async (draft: AreaDraft, specializationNames: string[]) => {
    if (editingArea) {
      const saved = await updateArea(editingArea.id, draft)
      if (saved) closeAreaSheet()
      return saved
    }

    const created = await createArea(draft)
    if (!created) return false
    trackEvent('area_created', language)
    for (const name of specializationNames) await createSpecialization(created.id, name)
    closeAreaSheet()
    openArea(created.id)
    return true
  }

  const removeArea = async () => {
    if (!editingArea) return false
    const deleted = await deleteArea(editingArea.id)
    if (deleted) {
      const wasOpen = areaId === editingArea.id
      closeAreaSheet()
      if (wasOpen) goHome()
    }
    return deleted
  }

  const openCreateAction = (targetAreaId: string, objectiveId?: string) => {
    clearError()
    setEditingAction(null)
    setActionAreaId(targetAreaId)
    setActionObjectiveId(objectiveId ?? null)
    setActionSheetOpen(true)
  }

  const openEditAction = (action: Action) => {
    clearError()
    setEditingAction(action)
    setActionAreaId(action.areaId)
    setActionObjectiveId(null)
    setActionSheetOpen(true)
  }

  const openAction = (action: Action, fromRandom = false) => {
    setPreviewActionId(action.id)
    setPreviewFromRandom(fromRandom)
  }

  const saveAction = async (draft: ActionDraft) => {
    if (!actionAreaId) return false
    const saved = editingAction ? await updateAction(editingAction.id, draft) : await createAction(actionAreaId, draft)
    if (saved) {
      if (!editingAction) trackEvent('action_created', language)
      setActionSheetOpen(false)
    }
    return saved
  }

  const removeAction = async () => {
    if (!editingAction) return false
    const deleted = await deleteAction(editingAction.id)
    if (deleted) {
      setActionSheetOpen(false)
      if (previewActionId === editingAction.id) setPreviewActionId(null)
    }
    return deleted
  }

  const revealActiveAction = () => window.requestAnimationFrame(() => {
    document.querySelector('.active-action-card')?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  })

  const activatePreview = async () => {
    if (!previewAction) return
    const activated = await activateAction(previewAction)
    if (activated) {
      trackEvent('action_started', language)
      setPreviewActionId(null)
      if (areaId !== previewAction.areaId) openArea(previewAction.areaId)
      revealActiveAction()
    }
  }

  const startCheckpointAction = async (action: Action) => {
    if (saving) return
    if (activeAction?.actionId === action.id) {
      setJournalObjectiveId(null)
      if (areaId !== action.areaId) openArea(action.areaId)
      revealActiveAction()
      return
    }
    if (activeAction && !window.confirm(missionCopy[language].replaceAction)) return
    if (await activateAction(action)) {
      trackEvent('action_started', language)
      setJournalObjectiveId(null)
      if (areaId !== action.areaId) openArea(action.areaId)
      revealActiveAction()
    }
  }

  const showRandomAction = (excludeId?: string) => {
    const available = selectedActions.filter((action) => action.repeatMode === 'repeatable' && action.id !== activeAction?.actionId)
    const candidates = available.filter((action) => action.id !== excludeId)
    const pool = candidates.length > 0 ? candidates : available
    if (pool.length === 0) return
    openAction(pool[Math.floor(Math.random() * pool.length)], true)
  }

  const finishActiveAction = async () => {
    const result = await completeActiveAction()
    if (result !== false) trackEvent('action_completed', language)
    if (result) setLevelUpEvent(result)
  }

  const addManagedSpecialization = async (name: string) => managedArea ? createSpecialization(managedArea.id, name) : false
  const removeManagedSpecialization = async (item: Specialization) => deleteSpecialization(item.id)

  const requestMissionOperation = (objective: Objective, operation: MissionOperation) => {
    clearError()
    setObjectivesSheetAreaId(null)
    setMissionOperation({ id: objective.id, operation })
  }
  const confirmMissionOperation = async () => {
    if (!missionOperation) return
    const saved = missionOperation.operation === 'complete' ? await completeObjective(missionOperation.id) : missionOperation.operation === 'close' ? await closeObjective(missionOperation.id) : await repeatObjective(missionOperation.id)
    if (saved) setMissionOperation(null)
  }

  const openObjectives = (targetAreaId: string) => { clearError(); setObjectivesSheetAreaId(targetAreaId) }
  const openObjectiveEditor = (targetAreaId: string, objective: Objective | null) => {
    clearError()
    setObjectivesSheetAreaId(null)
    setObjectiveEditorAreaId(targetAreaId)
    setEditingObjective(objective)
  }
  const saveObjective = async (draft: ObjectiveDraft) => {
    if (!objectiveEditorAreaId) return false
    return editingObjective ? updateObjective(editingObjective.id, draft) : createObjective(objectiveEditorAreaId, draft)
  }
  const openObjectiveLinks = (objective: Objective) => { setObjectivesSheetAreaId(null); setLinkObjectiveId(objective.id) }
  const openRewards = () => { clearError(); setRewardsSheetOpen(true) }
  const finishOnboarding = () => {
    try { localStorage.setItem('leveleria.onboarding.completed', 'true') } catch { /* IndexedDB remains the source of app data. */ }
    setOnboardingOpen(false)
    trackEvent('onboarding_completed', language)
  }
  const createRewardTracked = async (name: string, cost: number) => {
    const created = await createReward(name, cost)
    if (created) trackEvent('reward_created', language)
    return created
  }
  const redeemRewardTracked = async (id: string) => {
    const redeemed = await redeemReward(id)
    if (redeemed) trackEvent('reward_redeemed', language)
    return redeemed
  }
  const startFromOnboarding = () => { finishOnboarding(); openCreate() }

  const overlays = (
    <>
      <AreaSheet area={editingArea} open={areaSheetOpen} saving={saving} error={error} onClose={closeAreaSheet} onSave={saveArea} onDelete={editingArea ? removeArea : undefined} />
      <ActionSheet action={editingAction} specializations={specializations.filter((item) => item.areaId === actionAreaId)} initialObjectiveId={actionObjectiveId} areaColor={areas.find((area) => area.id === actionAreaId)?.color ?? '#5b8cff'} open={actionSheetOpen} saving={saving} error={error} onClose={() => setActionSheetOpen(false)} onSave={saveAction} onDelete={editingAction ? removeAction : undefined} />
      <ActionPreviewSheet action={previewAction} area={previewArea} hasActiveAction={Boolean(activeAction)} open={Boolean(previewAction)} saving={saving} specializationLabel={labelFor(previewAction)} onClose={() => setPreviewActionId(null)} onAnother={previewFromRandom ? () => showRandomAction(previewAction?.id) : undefined} onActivate={activatePreview} />
      <SpecializationsSheet area={managedArea} specializations={managedSpecializations} actions={managedActions} open={Boolean(managedArea)} saving={saving} error={error} onClose={() => setSpecializationsSheetAreaId(null)} onAdd={addManagedSpecialization} onDelete={removeManagedSpecialization} />
      <ObjectivesSheet onJournal={(objective) => { clearError(); setObjectivesSheetAreaId(null); setJournalObjectiveId(objective.id) }} area={objectivesArea} objectives={objectivesForArea} actions={objectiveActions} open={Boolean(objectivesArea)} saving={saving} onClose={() => setObjectivesSheetAreaId(null)} onCreate={() => objectivesArea && openObjectiveEditor(objectivesArea.id, null)} onEdit={(objective) => openObjectiveEditor(objective.areaId, objective)} onManageActions={openObjectiveLinks} onActivate={(objective) => activateObjective(objective.id)} onOperation={requestMissionOperation} onMove={(objective, delta) => moveObjective(objective.id, delta)} />
      <MissionLifecycleDialog objective={operationObjective} operation={missionOperation?.operation ?? 'complete'} saving={saving} error={error} onClose={() => { if (!saving) setMissionOperation(null) }} onConfirm={confirmMissionOperation} />
      <MissionJournalSheet objective={journalObjective} actions={missionActions} saving={saving} error={error} onClose={() => setJournalObjectiveId(null)} onSave={async (text, nextStep, action) => {
        if (!journalObjective) return false
        const saved = await addMissionNote(journalObjective.id, text, nextStep, action)
        if (saved && action.newActionName) trackEvent('action_created', language)
        return saved
      }} onAction={startCheckpointAction} onAdd={() => { if (journalObjective) { setJournalObjectiveId(null); openCreateAction(journalObjective.areaId, journalObjective.id) } }} onLink={() => { if (journalObjective) { setJournalObjectiveId(null); openObjectiveLinks(journalObjective) } }} onEdit={() => { if (journalObjective) { setJournalObjectiveId(null); openObjectiveEditor(journalObjective.areaId, journalObjective) } }} />
      <ObjectiveEditorSheet objective={editingObjective} open={Boolean(objectiveEditorAreaId)} saving={saving} error={error} onClose={() => { setObjectiveEditorAreaId(null); setEditingObjective(null) }} onSave={saveObjective} />
      <ObjectiveActionsSheet area={linkedArea} objective={linkedObjective} actions={linkedActions} specializations={linkedSpecializations} open={Boolean(linkedObjective)} saving={saving} onClose={() => setLinkObjectiveId(null)} onToggle={(action) => linkedObjective ? toggleActionObjective(linkedObjective.id, action.id) : Promise.resolve(false)} />
      <ThemeSheet open={themeSheetOpen} selected={themeMode} saving={saving} onClose={() => setThemeSheetOpen(false)} onSelect={selectThemeMode} onExport={exportBackup} onImport={importBackup} />
      <HistorySheet areas={areas} records={activityRecords} milestones={milestones} redemptions={rewardRedemptions} open={historySheetOpen} onClose={() => setHistorySheetOpen(false)} />
      <RewardsSheet coins={coins} rewards={rewards} targetRewardId={targetRewardId} open={rewardsSheetOpen} saving={saving} error={error} onClose={() => setRewardsSheetOpen(false)} onCreate={createRewardTracked} onRedeem={(reward) => redeemRewardTracked(reward.id)} onDelete={(reward) => deleteReward(reward.id)} onTarget={setTargetReward} />
      <LevelUpDialog event={levelUpEvent} onClose={() => setLevelUpEvent(null)} />
      <OnboardingDialog open={!loading && areas.length === 0 && onboardingOpen} onStart={startFromOnboarding} onExplore={finishOnboarding} />
      <OnboardingDialog returning open={!loading && helpOpen} onStart={() => setHelpOpen(false)} onExplore={() => setHelpOpen(false)} />
    </>
  )

  if (!loading && selectedArea) {
    return (
      <>
        <AreaDetail onOperation={requestMissionOperation} onEditObjective={(objective) => openObjectiveEditor(objective.areaId, objective)} onHelp={() => setHelpOpen(true)} missionActions={missionActions} records={activityRecords} error={error} onCheckpointAction={startCheckpointAction} onRandom={() => showRandomAction()} activeActionSlot={activeAction && activeActionData && activeAreaData?.id === selectedArea.id ? <ActiveActionCard action={activeActionData} area={selectedArea} active={activeAction} saving={saving} specializationLabel={labelFor(activeActionData)} onAmount={setActiveAmount} onComplete={finishActiveAction} onAbandon={async () => { await abandonActiveAction() }} /> : null} onJournal={(objective) => { clearError(); setJournalObjectiveId(objective.id) }} area={selectedArea} actions={selectedActions} specializations={selectedSpecializations} objectives={selectedObjectives} activeActionId={activeAction?.actionId ?? null} onBack={goHome} onEdit={openEdit} onManageSpecializations={() => { clearError(); setSpecializationsSheetAreaId(selectedArea.id) }} onManageObjectives={() => openObjectives(selectedArea.id)} onLinkObjective={openObjectiveLinks} onNewObjectiveAction={(objective) => openCreateAction(selectedArea.id, objective.id)} onNewAction={() => openCreateAction(selectedArea.id)} onOpenAction={startCheckpointAction} onEditAction={openEditAction} />
        {overlays}
      </>
    )
  }

  return (
    <main className="app-shell app-shell--home">
      <header className="app-header">
        <div className="wordmark" aria-label="Leveleria">
          <BrandMark />
          <span className="wordmark__name">Level<em>eria</em></span>
        </div>
        <div className="header-actions">
          <button className="icon-button icon-button--surface help-button" type="button" onClick={() => setHelpOpen(true)} aria-label={c.help}>?</button>
          <button className="coin-chip" type="button" onClick={openRewards} aria-label={`${coins} ${c.rewardsAria}`}><CoinsIcon />{coins}</button>
          <button className="icon-button icon-button--surface" type="button" onClick={() => setHistorySheetOpen(true)} aria-label={c.historyAria}><HistoryIcon /></button>
          <button className="icon-button icon-button--surface" type="button" onClick={() => setThemeSheetOpen(true)} aria-label={c.settingsAria}><SettingsIcon /></button>
        </div>
      </header>

      <div className="home-areas-heading"><p className="section-title">{c.yourAreas}</p>{areas.length > 0 && <button className="button button--outline" type="button" onClick={openCreate}><AddIcon />{c.newArea}</button>}</div>

      {error && <div className="error-message" role="alert">{error}</div>}
      {loading ? (
        <div className="loading-card" aria-label={c.loadingAreas} />
      ) : areas.length === 0 ? (
        <section className="empty-card">
          <CompassIcon />
          <h2>{c.createFirst}</h2>
          <p>{c.emptyHelp}</p>
          <button className="button button--primary empty-card__action" type="button" onClick={openCreate}>{c.createFirst}</button>
        </section>
      ) : (
        <section className="area-list" aria-label={c.yourAreas}>
          {areas.map((area) => <AreaCard key={area.id} area={area} objective={objectives.find((objective) => objective.areaId === area.id && objective.status === 'active') ?? null} actions={missionActions.filter((action) => action.areaId === area.id)} activeAction={activeActionData?.areaId === area.id ? activeActionData : null} onOpen={() => openArea(area.id)} onEdit={openEdit} />)}
        </section>
      )}

      {!loading && <RewardGoalCard reward={targetReward} coins={coins} hasRewards={rewards.length > 0} onOpen={openRewards} />}

      {overlays}
    </main>
  )
}
